import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, UserRound } from "lucide-react";
import { RESTAURANTS, ROLES } from "../constants";
import { HERO_BACKDROP_IMAGE } from "../mock/mock";
import { getCurrentUser, loadUsers, setSession } from "../lib/storage";
import { hashPin } from "../lib/crypto";
import LanguageToggle from "../components/LanguageToggle";
import { useLanguage } from "../i18n/LanguageContext";

const norm = (v) => String(v || "").trim().toLowerCase();
const nameUsername = (u) => norm(u.name).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/gi, ".").replace(/^\.|\.$/g, "");
const usernameAliases = (u) => [...new Set([
  norm(u.username),
  nameUsername(u),
  u.email?.includes("@") ? norm(u.email.split("@")[0]) : "",
].filter(Boolean))];
const destination = (u) => u.role === ROLES.MAIN ? "/main" : u.role === ROLES.RESTAURANT ? "/manager" : "/employee";

export default function SignIn() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const usernameRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const current = getCurrentUser();
    if (current) return navigate(destination(current), { replace: true });
    setUsers(loadUsers().filter((u) => u.active !== false));
    setTimeout(() => usernameRef.current?.focus(), 50);
  }, [navigate]);

  const byUsername = useMemo(
    () => new Map(users.flatMap((u) => usernameAliases(u).map((alias) => [alias, u]))),
    [users]
  );

  const submit = async (event) => {
    event.preventDefault();
    const user = byUsername.get(norm(username));
    if (!user || !/^\d{4}$/.test(pin)) return setError(t("badLogin"));
    setBusy(true);
    try {
      const hashed = await hashPin(pin);
      if (!((user.pinHash && user.pinHash === hashed) || (!user.pinHash && user.pin === pin))) {
        setPin("");
        return setError(t("badLogin"));
      }
      setSession(user.id, remember);
      navigate(destination(user), { replace: true });
    } finally { setBusy(false); }
  };

  const inputClass = "w-full h-[52px] rounded-xl border border-[#DED7C9] bg-white text-[#0B1F3A] outline-none focus:border-[#0B1F3A] focus:ring-2 focus:ring-[#0B1F3A]/10";
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white min-h-[30vh] lg:min-h-screen">
        <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center opacity-[0.22]" style={{ backgroundImage: `url(${HERO_BACKDROP_IMAGE})` }} />
        <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/90 via-[#0B1F3A]/75 to-[#071530]/95" />
        <div className="relative z-10 flex flex-col justify-between min-h-[30vh] lg:min-h-screen px-6 md:px-14 py-7 md:py-10">
          <div className="flex items-center gap-4"><img src="/brand-mark.png" alt="Café Breizh · JGI Stock Operation" className="h-14 w-14 md:h-20 md:w-20 rounded-2xl shadow-xl" /><div><p className="text-[11px] tracking-eyebrow uppercase text-white/70">JGI Stock Operation</p><p className="text-lg md:text-2xl font-serif-display">Café Breizh</p></div></div>
          <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] font-light mt-8 lg:mt-0">{t("purchasing")}<br /><span className="italic text-[#F07C52]">{t("operations")}</span></h1>
          <p className="hidden lg:block text-[11px] tracking-eyebrow uppercase text-white/60">{RESTAURANTS.map((r) => r.name).join(" · ")}</p>
        </div>
      </section>
      <main className="bg-[#F3EFE7] flex items-start lg:items-center justify-center px-5 md:px-10 py-10">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center justify-between mb-3"><p className="text-[11px] tracking-eyebrow uppercase text-[#0B1F3A]/60">{t("signIn")}</p><LanguageToggle /></div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#0B1F3A]">{t("welcome")}</h2>
          <p className="mt-2 text-sm text-[#0B1F3A]/65">{t("loginHelp")}</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <Field label={t("username")}><UserRound className="icon" size={18} /><input ref={usernameRef} value={username} onChange={(e) => { setUsername(e.target.value); setError(""); }} autoCapitalize="none" autoCorrect="off" autoComplete="username" aria-label={t("username")} className={`${inputClass} pl-11 pr-4`} placeholder={t("yourUsername")} /></Field>
            <Field label="PIN"><Lock className="icon" size={18} /><input value={pin} onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(""); }} type={showPin ? "text" : "password"} inputMode="numeric" maxLength={4} autoComplete="current-password" aria-label="4-digit PIN" className={`${inputClass} pl-11 pr-12 text-lg tracking-[0.45em]`} placeholder="••••" /><button type="button" onClick={() => setShowPin((v) => !v)} aria-label={showPin ? "Hide PIN" : "Show PIN"} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0B1F3A]/55">{showPin ? <EyeOff size={18} /> : <Eye size={18} />}</button></Field>
            <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mt-1 accent-[#0B1F3A]" /><span><b className="block text-sm text-[#0B1F3A]">{t("remember")}</b><span className="text-xs text-[#0B1F3A]/55">{t("rememberHelp")}</span></span></label>
            <p role="alert" className={`text-sm min-h-5 ${error ? "text-red-600" : "text-transparent"}`}>{error || "No error"}</p>
            <button disabled={busy || !username.trim() || pin.length !== 4} className="w-full h-[52px] rounded-xl bg-[#0B1F3A] text-white font-medium disabled:opacity-45">{busy ? t("signingIn") : t("signIn")}</button>
          </form>
          <p className="mt-7 text-xs text-[#0B1F3A]/50">{t("accountScope")}</p>
        </div>
      </main>
      <style>{`.icon{position:absolute;left:.875rem;top:50%;transform:translateY(-50%);color:rgba(11,31,58,.45)}`}</style>
    </div>
  );
}
function Field({ label, children }) { return <label className="block"><span className="text-[10.5px] tracking-eyebrow uppercase text-[#0B1F3A]/60">{label}</span><div className="relative mt-2">{children}</div></label>; }
